//region Errors
export class TokenNotFoundError extends Error {
	constructor() {
		super('Token not found');
	}
}

export class FailedAuthError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed authentication: ' + message);
	}
}

export class FailedGetBillError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get bill: ' + message);
	}
}

export class FailedGetBillsError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get bills: ' + message);
	}
}

export class FailedGetPaymentProvidersError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get payment providers: ' + message);
	}
}

export class FailedGetPaymentTariffsError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get payment tariffs: ' + message);
	}
}

export class FailedCreatePaymentInvoiceError extends Error {
	code?: string;
	constructor(message: string = 'Unknown error', code?: string) {
		super('Failed create payment invoice: ' + message);
		this.code = code;
	}
}

//endregion

//region Auth
export interface LoginInfo {
	token: string;
	user: User
	// Set by the backend when this auth created the account, so the client can
	// emit `sign_up` instead of `login`. Absent on backends that don't send it
	// yet — the client then falls back to `login` for everyone.
	is_new_user?: boolean;
}

export interface OtpSendResponse {
	retry_after: number;
}

export interface TelegramAuthData {
	success: boolean,
	data: {
		token:string,
		user: User
	},
	message: string
}
//endregion

//region SocialProviders
export type SocialProvider = 'telegram' | 'google';
export type SocialProviderStatus = 'success' | 'failed' | 'waiting';
export type SocialProviderType = 'auth' | 'connect';
export interface UserConnectNotification {
	status?: SocialProviderStatus;
	provider?: SocialProvider;
	code?: string;
}
//endregion

//region User
export type SubscriptionStatus = 'active' | 'expired' | 'trial';
export interface User {
	id: number;
	email?: string;
	email_verified_at?: Date;
	lang: string;
	subscription: Subscription;
	onboarding_showed_at?: Date;
	telegram: boolean;
	google: boolean;
	apple: boolean;
	term_of_use: boolean;
	term_of_use_date?: Date;
	promo_code?: PromoCode | null;
	// Mirrors LoginInfo.is_new_user for auth responses that carry the flag on
	// the user object instead of alongside it.
	is_new_user?: boolean;
}

export interface Subscription {
	status: SubscriptionStatus;
	end: string;
	auto_payment?: Price;
}

export interface UserUpdate {
	email?: string;
	lang?: string;
	auto_payment?: boolean;
}

export interface Referral {
	code: string;
	link: string;
	activations: number;
	paid: number;
}
//endregion

//region VPN
export interface KeysInfo {
	sub_link: string;
	sub_link_new?: string;
	keys: Array<Key>;
}

export interface Key {
	country?: Country|null;
	protocol: string;
	link: string;
}

export interface Country {
	name: string;
	code: string;
	emoji: string;
}
//endregion

//region Billing, Payment, Price
export type Currency = 'rub' | 'usdt' | 'xtr' | 'ton' | 'usd';
export type PaySystem = 'yoomoney' | 'yookassa' | 'yookassa_web_app' | 'crypto_pay' | 'wallet_pay' | 'telegram_stars' | 'freekassa' | 'platega';
export type RubMode = 'split' | 'auto' | null;
export type BillStatus = 'created' | 'paid' | 'refund';
// Что оплачено счётом. `whitelist_topup` — пакет резервного трафика.
// Бэкенд отдаёт null, если бот записал значение, которого кабинет ещё не знает.
export type BillType = 'subscription' | 'donation' | 'whitelist_topup';

export interface Price {
	amount: number;
	currency: Currency;
}

export interface Bill {
	id: number;
	cost: Price;
	subscription_months: number;
	// Отсутствуют у старых счетов и на бэкендах без резерва — отсюда `?` и `null`.
	type?: BillType | null;
	// Снимок резерва в счёте: порция ГБ/мес у подписки с резервом,
	// размер пакета у `whitelist_topup`.
	whitelist_gb?: number | null;
	pay_system: PaySystem;
	status: BillStatus;
	paid_at?: Date;
	refund_at?: Date;
	created_at?: Date;
}

export interface PaymentTariff {
	id: number;
	cost: Price;
	cost_without_discount?: Price | null;
	promo_code?: PromoCode | null;
	months: number;
	// Сколько ГБ резервного трафика бот начисляет каждый месяц по этой подписке,
	// пока она активна. Это не объём на весь срок. `null` — резерв в тариф не входит
	// (продажи резерва выключены), тогда строку в карточке не показываем.
	reserve_monthly_gb?: number | null;
	support_providers: PaymentProvider[];
}

export interface PaymentProvider {
	code: PaySystem;
	support_currencies: Currency[];
	rub_mode?: RubMode;
}

export interface PaymentInvoice {
	link: string;
	bill: Bill;
	// HMAC token for sessionless bill-status polling (checkout without a session)
	status_token?: string;
}

export type PromoCodeType = 'discount' | 'gift_month';

export interface PromoCode {
	code: string;
	type: PromoCodeType;
	discount: number | null;
	gift_months: number | null;
}

export class FailedCheckPromoCodeError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed check promo code: ' + message);
	}
}

export class FailedApplyPromoCodeError extends Error {
	code?: string;
	constructor(message: string = 'Unknown error', code?: string) {
		super('Failed apply promo code: ' + message);
		this.code = code;
	}
}
//endregion

//region Reserve
/**
 * Состояние кошелька резервного трафика. Кошелёк ведёт бот, кабинет только читает.
 * Единственный признак «резерв выдан» — `issued` (он же `state` active|grace).
 * Выводить выдачу из блока `whitelist` нельзя: там `null` означает «нет данных».
 */
export type ReserveWalletState = 'none' | 'active' | 'grace' | 'burned';

export interface ReserveWallet {
	// Строка, а не только union: бот может завести новое состояние, и кабинет
	// не должен на этом падать — тогда `issued` придёт false и блок просто скрыт.
	state: ReserveWalletState | string;
	issued: boolean;
	monthly_gb: number;
	// F — бесплатных выдано в текущем цикле, байты
	free_granted_bytes: number;
	// P — куплено пакетами, байты
	purchased_bytes: number;
	// Дата следующего начисления бесплатных ГБ: «Обновятся 8 октября» на странице резерва
	next_accrual_at?: string | null;
	grace_until?: string | null;
}

export type ReserveWhitelistStatus = 'ACTIVE' | 'LIMITED' | 'DISABLED' | 'EXPIRED' | 'UNKNOWN';

/**
 * Состояние резервной подписки в keymanager, best-effort.
 *
 * Поля `sub_link` здесь намеренно нет: подписочная ссылка у пользователя одна — та,
 * что кабинет показывает сегодня. Форк страницы подписки уже отдаёт по ней
 * объединённый конфиг (основные ключи + резервные), так что вторая ссылка не нужна
 * и показывать её нельзя.
 */
export interface ReserveWhitelist {
	status: ReserveWhitelistStatus | string;
	enable: boolean;
	sub_expire?: string | null;
	traffic_limit_bytes: number;
	traffic_used_bytes: number;
}

/** Остаток резервного трафика в байтах; считается на сервере теми же формулами, что в боте. */
export interface ReserveRemaining {
	total_remaining_bytes: number;
	used_cycle_bytes: number;
	free_remaining_bytes: number;
	purchased_remaining_bytes: number;
}

/**
 * Ответ `GET /reserve`. Каждый источник best-effort: `null` в `wallet`/`whitelist`/
 * `remaining` означает «данных нет», а не «резерва нет».
 */
export interface ReserveState {
	sales_enabled: boolean;
	can_buy_pack: boolean;
	// false — keymanager не ответил: `whitelist` пуст, а остаток посчитан при used = 0,
	// то есть показан приблизительно.
	key_manager_available: boolean;
	wallet: ReserveWallet | null;
	whitelist: ReserveWhitelist | null;
	remaining: ReserveRemaining | null;
}

/** Пакет резервного трафика. Скидок нет: промокоды и рефералки к пакетам не применяются. */
export interface ReservePack {
	id: number;
	gb: number;
	cost: Price;
	// Ровно один провайдер — тот, что резолвер выбрал для валюты пакета.
	support_providers: PaymentProvider[];
}

export class FailedGetReserveStateError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get reserve state: ' + message);
	}
}

export class FailedGetReservePacksError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get reserve packs: ' + message);
	}
}

export class FailedCreateReservePackInvoiceError extends Error {
	code?: string;
	constructor(message: string = 'Unknown error', code?: string) {
		super('Failed create reserve pack invoice: ' + message);
		this.code = code;
	}
}
//endregion

//region Traffic
/**
 * Один блок трафика: основной ключ или резервное подключение. Все числа —
 * байты, как в GET /reserve. `null` в поле — данных нет, ноль — это ноль.
 */
export interface TrafficBlock {
	// Потрачено с последней оплаты подписки: бот сбрасывает счётчик панели на
	// каждой оплате подписки (покупка пакета ГБ его не трогает). Даты сброса
	// в ответе нет.
	period_used_bytes: number | null;
	// Лимит периода: у основного ключа — триальный, null = безлимит;
	// у резерва — норма месяца плюс купленное
	period_limit_bytes: number | null;
	// За всё время
	lifetime_used_bytes: number | null;
}

/**
 * Ответ `GET /traffic` для строки на главной. Считает бэкенд кабинета через
 * keymanager и кэширует: это аналитический запрос, а не поле записи.
 *
 * `main: null` — ключи ещё не выданы; `whitelist: null` — резерв не выдан.
 * keymanager не ответил → 200 с `key_manager_available: false` и пустыми
 * блоками, ошибок на «нет данных» не бывает.
 */
export interface TrafficStats {
	key_manager_available: boolean;
	main: TrafficBlock | null;
	whitelist: TrafficBlock | null;
}

export class FailedGetTrafficStatsError extends Error {
	constructor(message: string = 'Unknown error') {
		super('Failed get traffic stats: ' + message);
	}
}
//endregion

//region Settings
export type AppType = 'streisand' | 'hiddify' | 'v2box' | 'outline' | 'v2ray_ng';
export type HelpCode = 'news' | 'terms_of_use' | 'support' | 'faq';

export interface SettingsInfo {
	available_lang: Array<string>;
}

export interface AppInfo {
	name: string;
	code: AppType|string;
	platform_code: string;
	platform_name: string;
	download_link?: string;
	instruction_link: string;
}

export interface HelpLink {
	code: HelpCode|string;
	link: string;
}

export interface AppChoose {
	app: string;
	platform: string;
}
//endregion




